import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ScatterChart as ScatterChartBase,
  Scatter as ScatterBase,
  XAxis as XAxisBase,
  YAxis as YAxisBase,
  CartesianGrid as CartesianGridBase,
  Tooltip as TooltipBase,
  ResponsiveContainer as ResponsiveContainerBase,
  ReferenceLine as ReferenceLineBase,
  Legend as LegendBase,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import apiClient from '../../api/client';
import { Molecule } from '../../types/chemistry';
import { getMoleculeName } from './MoleculeGrid';
import { formatAxisLabel } from '../../utils/unitConversion';

// Recharts 3.x types don't align with React 18 JSX — cast to bypass
const SChart = ScatterChartBase as any;
const SScatter = ScatterBase as any;
const SXAxis = XAxisBase as any;
const SYAxis = YAxisBase as any;
const SGrid = CartesianGridBase as any;
const STooltip = TooltipBase as any;
const SContainer = ResponsiveContainerBase as any;
const SRefLine = ReferenceLineBase as any;
const SLegend = LegendBase as any;

interface Props {
  molecules: Molecule[];
  onMoleculeClick?: (moleculeId: number) => void;
  columnUnitsMap?: Record<string, { unit: string | null; unitType: string | null; displayName: string | null }>;
}

interface CalcProps {
  [key: string]: number | undefined;
}

const BUILTIN_OPTIONS: { key: string; label: string; refLine?: number }[] = [
  { key: 'mw', label: 'MW (Da)', refLine: 500 },
  { key: 'logp', label: 'LogP', refLine: 5 },
  { key: 'tpsa', label: 'TPSA (Å²)', refLine: 140 },
  { key: 'hbd', label: 'HBD', refLine: 5 },
  { key: 'hba', label: 'HBA', refLine: 10 },
  { key: 'rotatable_bonds', label: 'Rotatable Bonds', refLine: 10 },
  { key: 'ring_count', label: 'Ring Count' },
  { key: 'fsp3', label: 'Fsp3' },
];

const EXCLUDED_KEYS = new Set(['id', 'file_id', 'smiles', 'molfile', 'inchi', 'structure', 'svg', 'image']);
const BUILTIN_KEYS = new Set(BUILTIN_OPTIONS.map(o => o.key));

// Map builtin keys to alternate property names used by the API
const KEY_ALIASES: Record<string, string[]> = {
  mw: ['MW', 'molecular_weight', 'MolecularWeight'],
  logp: ['LogP', 'logP', 'LOGP', 'ALogP'],
  tpsa: ['TPSA', 'Tpsa'],
  hbd: ['HBD', 'num_h_donors', 'NumHDonors'],
  hba: ['HBA', 'num_h_acceptors', 'NumHAcceptors'],
  rotatable_bonds: ['RotBonds', 'Rotatable Bonds', 'NumRotatableBonds'],
  ring_count: ['Rings', 'Ring Count', 'RingCount', 'num_rings'],
  fsp3: ['Fsp3', 'FSP3'],
};

// Organic molecule filter: must contain C, only allowed elements
const ORGANIC_ELEMENTS = new Set(['C', 'H', 'N', 'O', 'S', 'P', 'F', 'Cl', 'Br', 'I']);
const FORMULA_TOKEN_RE = /([A-Z][a-z]?)(\d*)/g;

const isOrganicFormula = (formula: string | undefined): boolean => {
  if (!formula) return false;
  let hasCarbon = false;
  let match: RegExpExecArray | null;
  FORMULA_TOKEN_RE.lastIndex = 0;
  while ((match = FORMULA_TOKEN_RE.exec(formula)) !== null) {
    const elem = match[1];
    if (!elem) continue;
    if (elem === 'C') hasCarbon = true;
    if (!ORGANIC_ELEMENTS.has(elem)) return false; // metal or disallowed element
  }
  return hasCarbon;
};

const BATCH_SIZE = 10;

const SARScatterChart: React.FC<Props> = ({ molecules, onMoleculeClick, columnUnitsMap }) => {
  const [propsData, setPropsData] = useState<Record<number, CalcProps>>({});
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [xKey, setXKey] = useState<string>('mw');
  const [yKey, setYKey] = useState<string>('logp');

  // Dynamically discover numeric columns from molecule properties
  const allOptions = useMemo(() => {
    const extra: { key: string; label: string; refLine?: number }[] = [];
    // Build set of all alias names so we don't duplicate builtins
    const aliasSet = new Set<string>();
    Object.values(KEY_ALIASES).forEach(aliases => aliases.forEach(a => aliasSet.add(a)));

    if (molecules.length > 0) {
      const props = molecules[0].properties || {};
      Object.keys(props).forEach(key => {
        if (EXCLUDED_KEYS.has(key) || BUILTIN_KEYS.has(key) || aliasSet.has(key)) return;
        const val = props[key];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '' && !isNaN(Number(val)))) {
          extra.push({ key, label: key });
        }
      });
    }
    return [...BUILTIN_OPTIONS, ...extra];
  }, [molecules]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProgress(0);

    const fetchAll = async () => {
      const propMap: Record<number, CalcProps> = {};
      const mols = molecules.filter((m) => m.smiles);

      for (let i = 0; i < mols.length; i += BATCH_SIZE) {
        if (cancelled) return;
        const batch = mols.slice(i, i + BATCH_SIZE);

        const results = await Promise.all(
          batch.map(async (mol) => {
            try {
              const resp = await apiClient.post('/v2/rdkit/properties/calculate', { smiles: mol.smiles });
              // Map RDKit response keys to our standard keys
              const raw = resp.data || {};
              const mapped: CalcProps = {
                mw: raw.mol_weight ?? raw.mw,
                logp: raw.logp,
                tpsa: raw.tpsa,
                hbd: raw.hbd,
                hba: raw.hba,
                rotatable_bonds: raw.rotatable_bonds,
                ring_count: raw.num_rings ?? raw.ring_count,
                fsp3: raw.fsp3,
                _organic: isOrganicFormula(raw.mol_formula) ? 1 : 0,
              };
              return { id: mol.id, data: mapped };
            } catch {
              return { id: mol.id, data: {} as CalcProps };
            }
          }),
        );

        if (cancelled) return;
        results.forEach((r) => { propMap[r.id] = r.data; });
        setPropsData({ ...propMap });
        setProgress(Math.min(i + BATCH_SIZE, mols.length));
      }

      if (!cancelled) setLoading(false);
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [molecules]);

  const isLipinskiPass = useCallback((p: CalcProps) => {
    let violations = 0;
    if (p.mw != null && p.mw > 500) violations++;
    if (p.logp != null && p.logp > 5) violations++;
    if (p.hbd != null && p.hbd > 5) violations++;
    if (p.hba != null && p.hba > 10) violations++;
    return violations <= 1;
  }, []);

  // Get a numeric value: check calculated propsData first, then mol.properties (with alias fallback)
  const getVal = useCallback((mol: Molecule, p: CalcProps | undefined, key: string): number | null => {
    // 1. Calculated properties (exact key)
    if (p && (p as any)[key] != null) return (p as any)[key];
    // 2. mol.properties exact key
    const raw = mol.properties?.[key];
    if (typeof raw === 'number') return raw;
    if (raw != null) { const n = parseFloat(raw); if (!isNaN(n)) return n; }
    // 3. Try alias keys (e.g. 'mw' → 'MW')
    const aliases = KEY_ALIASES[key];
    if (aliases && mol.properties) {
      for (const alias of aliases) {
        const v = mol.properties[alias];
        if (typeof v === 'number') return v;
        if (v != null) { const n = parseFloat(v); if (!isNaN(n)) return n; }
      }
    }
    // 4. Check top-level molecule fields (mw, logp, etc. from spread)
    const topLevel = (mol as any)[key];
    if (typeof topLevel === 'number') return topLevel;
    if (topLevel != null) { const n = parseFloat(topLevel); if (!isNaN(n)) return n; }
    return null;
  }, []);

  const { passData, failData, skippedInorganic } = useMemo(() => {
    const pass: any[] = [];
    const fail: any[] = [];
    let skipped = 0;
    molecules.forEach((mol) => {
      const p = propsData[mol.id];
      // Skip non-organic molecules (metals, inorganic complexes)
      if (p && p._organic === 0) { skipped++; return; }
      const xVal = getVal(mol, p, xKey);
      const yVal = getVal(mol, p, yKey);
      if (xVal == null || yVal == null) return;
      const name = getMoleculeName(mol.properties) || mol.smiles?.slice(0, 25) || '?';
      const point = { x: xVal, y: yVal, name, id: mol.id };
      if (isLipinskiPass(p || {})) {
        pass.push(point);
      } else {
        fail.push(point);
      }
    });
    return { passData: pass, failData: fail, skippedInorganic: skipped };
  }, [molecules, propsData, xKey, yKey, isLipinskiPass, getVal]);

  const xOption = allOptions.find((o) => o.key === xKey);
  const yOption = allOptions.find((o) => o.key === yKey);

  const getLabel = useCallback((key: string, fallbackLabel?: string) => {
    if (columnUnitsMap) {
      const entry = columnUnitsMap[key];
      if (entry) return formatAxisLabel(key, entry.unit);
    }
    return fallbackLabel || key;
  }, [columnUnitsMap]);

  const handleDotClick = useCallback(
    (data: any) => {
      if (data?.id && onMoleculeClick) {
        onMoleculeClick(data.id);
      }
    },
    [onMoleculeClick],
  );

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const d = payload[0].payload;
    return (
      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: 11,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          {d.name}
        </div>
        <div style={{ color: 'var(--text-muted)' }}>
          {xOption?.label}: <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>{typeof d.x === 'number' ? d.x.toFixed(2) : d.x}</span>
        </div>
        <div style={{ color: 'var(--text-muted)' }}>
          {yOption?.label}: <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>{typeof d.y === 'number' ? d.y.toFixed(2) : d.y}</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 16 }}>
      {/* Controls */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 12,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>X Axis</span>
          <select
            value={xKey}
            onChange={(e) => setXKey(e.target.value)}
            style={{
              fontSize: 12,
              padding: '4px 8px',
              borderRadius: 6,
              border: '1px solid var(--card-border)',
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          >
            {allOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Y Axis</span>
          <select
            value={yKey}
            onChange={(e) => setYKey(e.target.value)}
            style={{
              fontSize: 12,
              padding: '4px 8px',
              borderRadius: 6,
              border: '1px solid var(--card-border)',
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          >
            {allOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Loader2 size={14} className="animate-spin" style={{ color: '#0D9488' }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Calculating {progress}/{molecules.length}...
            </span>
          </div>
        )}
        {!loading && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {passData.length + failData.length} organic compounds plotted
            <span style={{ color: '#16a34a', fontWeight: 600, marginLeft: 8 }}>● {passData.length} Lipinski pass</span>
            <span style={{ color: '#dc2626', fontWeight: 600, marginLeft: 8 }}>● {failData.length} fail</span>
            {skippedInorganic > 0 && (
              <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>({skippedInorganic} inorganic excluded)</span>
            )}
          </span>
        )}
      </div>

      {/* Chart */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <SContainer width="100%" height="100%">
          <SChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
            <SGrid strokeDasharray="3 3" stroke="var(--card-border)" />
            <SXAxis
              type="number"
              dataKey="x"
              name={getLabel(xKey, xOption?.label)}
              label={{
                value: getLabel(xKey, xOption?.label),
                position: 'insideBottom',
                offset: -10,
                style: { fill: 'var(--text-muted)', fontSize: 12 },
              }}
              tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
              stroke="var(--card-border)"
            />
            <SYAxis
              type="number"
              dataKey="y"
              name={getLabel(yKey, yOption?.label)}
              label={{
                value: getLabel(yKey, yOption?.label),
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                style: { fill: 'var(--text-muted)', fontSize: 12 },
              }}
              tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
              stroke="var(--card-border)"
            />
            <STooltip content={<CustomTooltip />} />
            <SLegend
              wrapperStyle={{ fontSize: 11 }}
            />
            {/* Reference lines for Lipinski limits */}
            {xOption?.refLine != null && (
              <SRefLine
                x={xOption.refLine}
                stroke="#dc2626"
                strokeDasharray="6 4"
                strokeOpacity={0.5}
                label={{
                  value: `${xOption.label} = ${xOption.refLine}`,
                  position: 'top',
                  style: { fill: '#dc2626', fontSize: 10, opacity: 0.7 },
                }}
              />
            )}
            {yOption?.refLine != null && (
              <SRefLine
                y={yOption.refLine}
                stroke="#dc2626"
                strokeDasharray="6 4"
                strokeOpacity={0.5}
                label={{
                  value: `${yOption.label} = ${yOption.refLine}`,
                  position: 'right',
                  style: { fill: '#dc2626', fontSize: 10, opacity: 0.7 },
                }}
              />
            )}
            <SScatter
              name="Lipinski Pass"
              data={passData}
              fill="#16a34a"
              fillOpacity={0.7}
              shape="circle"
              onClick={handleDotClick}
              cursor="pointer"
            />
            <SScatter
              name="Lipinski Fail"
              data={failData}
              fill="#dc2626"
              fillOpacity={0.7}
              shape="diamond"
              onClick={handleDotClick}
              cursor="pointer"
            />
          </SChart>
        </SContainer>
      </div>
    </div>
  );
};

export default SARScatterChart;
